import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, UserCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Blocks the app with a full-screen overlay until the user uploads a profile photo.
 * Shown for employees whose photo_url is empty.
 * Admins are never blocked.
 */
export const ProfilePhotoGate = ({ user, onUploaded }) => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  if (!user) return null;
  if (user.role === 'admin') return null;
  if (user.photo_url) return null;

  const handleSelect = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error('Photo trop lourde (max 5 Mo)'); return; }
    setFile(f);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(f));
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Choisissez une photo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/users/${user.id}/photo`, fd, { withCredentials: true });
      toast.success('Photo de profil enregistree');
      if (preview) URL.revokeObjectURL(preview);
      onUploaded && onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally { setUploading(false); }
  };

  return (
    <div data-testid="profile-photo-gate" style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      background: 'linear-gradient(135deg, #002FA7 0%, #1E3A8A 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'white', borderRadius: '14px', maxWidth: '440px', width: '100%',
        padding: '32px 28px', textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }}>
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%', background: '#E3F2FD',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
        }}>
          <UserCircle size={40} color="#002FA7" weight="fill" />
        </div>
        <h2 style={{ margin: '0 0 8px', fontSize: '1.4rem', color: '#0A0B10' }}>Bienvenue {user.name?.split(' ')[0] || ''}</h2>
        <p style={{ margin: '0 0 22px', color: '#5E5F66', fontSize: '0.92rem', lineHeight: 1.5 }}>
          Pour continuer, merci d'ajouter une photo de profil.
          Elle sera visible par l'administration et sur vos pointages.
        </p>

        {preview ? (
          <div style={{ marginBottom: '18px' }}>
            <img
              src={preview}
              alt="Aperçu"
              style={{ width: '140px', height: '140px', borderRadius: '50%', objectFit: 'cover', border: '4px solid #E3F2FD', margin: '0 auto', display: 'block' }}
            />
          </div>
        ) : (
          <div style={{ marginBottom: '18px' }}>
            <div style={{ width: '140px', height: '140px', borderRadius: '50%', background: '#F4F5F7', border: '2px dashed #C7D0E8', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Camera size={40} color="#9A9AA0" />
            </div>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="user"
          onChange={handleSelect}
          style={{ display: 'none' }}
          data-testid="profile-photo-input"
        />

        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="btn btn-secondary"
            data-testid="profile-photo-select"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <Camera size={16} /> {file ? 'Changer la photo' : 'Choisir une photo'}
          </button>
          <button
            type="button"
            onClick={handleUpload}
            className="btn btn-primary"
            disabled={!file || uploading}
            data-testid="profile-photo-upload"
          >
            {uploading ? 'Enregistrement...' : 'Enregistrer et continuer'}
          </button>
        </div>

        <p style={{ margin: '18px 0 0', fontSize: '0.75rem', color: '#9A9AA0' }}>
          JPG, PNG ou WEBP - 5 Mo maximum
        </p>
      </div>
    </div>
  );
};

export default ProfilePhotoGate;
